// Login.js
import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';

// Material UI
import { 
  TextField, 
  Button, 
  IconButton, 
  Typography, 
  InputAdornment, 
  Link as MuiLink 
} from '@mui/material';

// Icons
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

// Firebase
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebaseConfig';

// Authentication helpers
import { authenticatedFetch, handleAuthError } from '../utils/authHelpers'

// Local CSS module
import styles from './LoginPage.module.css';

/**
 * Login – renders the authentication form for GigemAI.
 * 
 * Features:
 * - Email & password fields with real‑time error clearing.
 * - Firebase Auth sign‑in (`handleLogin`):
 *     • Calls `signInWithEmailAndPassword`.
 *     • Checks email verification.
 *     • Navigates to `/upload` on success.
 *     • Handles common Firebase auth errors with user‑friendly messages.
 * - "Forgot Password" flow (`handleForgotPassword`):
 *     • Sends password reset email via Firebase.
 *     • Provides success or error feedback.
 * - Toggle password visibility (`toggleShowPassword`).
 * - Submit on Enter key (`handleKeyDown`).
 * 
 * State:
 * - `email`, `password`, `error`, `showPassword`.
 * 
 * Hooks & Context:
 * - `useNavigate` for routing.
 * - Firebase `auth` instance for sign‑in and reset.
 * 
 * Author: Galo Alonso Peralta Espinoza
 * Date: 04-17-2025
 */

function Login() {

  // State variables
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const navigate = useNavigate();

  //** Toggle Password Logic **//
  const toggleShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  //** Credentials Change Logic **//
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setError('');
  };
  const handlePasswordChange = (e) => {
    setPassword(e.target.value);
    setError('');
  };

  //** Login Logic **//
  const handleLogin = async () => {

    //? 1. Attempt Firebase sign in
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      //? 2. Check if user is verified
      if (!userCredential.user.emailVerified) {
        setError('Please verify your email before logging in.');
        return;
      }

      //? 3. Check subscription status and redirect accordingly
      try {
        const subscriptionResponse = await authenticatedFetch('/api/user/subscription-status');
        if (!subscriptionResponse.ok) {
          throw new Error('Failed to check subscription status');
        }
        
        const { subscription } = await subscriptionResponse.json();
        
        //? 4. Navigate based on subscription status
        if (subscription?.active) {
          
          //? Optionally ensure Firestore user document exists for subscribed users
          try {
            const ensureResponse = await authenticatedFetch('/api/user/ensure', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' }
            });
            if (!ensureResponse.ok) {
              console.warn('Ensure user document returned non-OK response');
            }
          } catch (e) {
            console.warn('Ensure user document failed (continuing):', e);
          }

          navigate('/upload'); // Has subscription - go to app
        } else {
          console.log('Subscription inactive for user. Redirecting to /subscribe');
          navigate('/subscribe'); // No subscription - go to subscribe
        }
      } catch (error) {
        console.error('Failed to check subscription status:', error);
        // Graceful handling similar to SubscribePage
        const message = (error && error.message) ? error.message : '';
        if (message.includes('New user - no subscription document')) {
          console.log('Detected brand-new user with no subscription document. Redirecting to /subscribe');
          navigate('/subscribe');
        } else if (
          message.includes('not authenticated') ||
          message.includes('Authentication expired') ||
          message.includes('Session expired') ||
          message.includes('Authentication failed')
        ) {
          const userMessage = handleAuthError(error, 'check subscription status');
          setError(userMessage);
          // Send to login if auth is invalid
          navigate('/login');
        } else if (
          message.includes('Failed to fetch') ||
          message.includes('NetworkError') ||
          message.includes('fetch')
        ) {
          setError('Network error while checking subscription. Please try again.');
          // Safe default: route to subscribe so users can proceed
          navigate('/subscribe');
        } else {
          // Unknown/server error – default to subscribe with a minimal hint
          setError('Unable to verify subscription right now. You may subscribe to continue.');
          navigate('/subscribe');
        }
      }

      //? 5. Catch any errors
    } catch (error) {
      switch (error.code) {
        case 'auth/user-not-found':
          setError('User not found. Please register.');
          break;
        case 'auth/wrong-password':
          setError('Incorrect password. Please try again.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email format. Please try again.');
          break;
        case 'auth/invalid-credential':
          setError('Invalid credentials. Please double-check or sign up if new.');
          break;
        case 'auth/missing-password':
          setError('Please enter a password.');
          break;
        default:
          setError(error.message);
      }
    }
  };

  // Forgot Password Logic
  const handleForgotPassword = async () => {

    if (!email) {
      setError('Please enter your email first, then click "Forgot Password".');
      return;
    }

    try {
      await sendPasswordResetEmail(auth, email);
      setError('Password reset email sent. Please check your inbox.');
    } catch (error) {
      switch (error.code) {
        case 'auth/user-not-found':
          setError('User not found. Please register.');
          break;
        case 'auth/invalid-email':
          setError('Invalid email format. Please try again.');
          break;
        default:
          setError(error.message);
      }
    }
  };

  // Handle Enter key
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

    return (
      <main className={styles.loginPage}>

        {/* Navbar */}  
        <header className={styles.navbar}>
          <div className={styles.navbarContent}>
            <img
              className={styles.navLogo}
              alt="GigemAI Logo"
              src="gigemai_logo_v2.png"
            />
          </div>
        </header>
  
        {/* Main content area */}
        <section className={styles.loginContent}>
          
          {/* Login Header */}
          <div className={styles.loginHeader}>
            <h3 className={styles.loginTitle}>
              ExtractAI
            </h3>
            <p className={styles.loginSubtitle}>
              Login
            </p>
          </div>

          {/* Login Form Glassmorphism */}
          <div className={styles.loginFormGlass}>
            <div className={styles.loginFormFieldsCentered}>
              {/* EMAIL Field */}
              <div className={styles.loginFormGroupCentered}>
                <Typography
                  sx={{
                    color: '#1E1E1E',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '22.4px',
                    marginBottom: '8px',
                  }}
                >
                  Email
                </Typography>
                <TextField
                  placeholder="user@example.com"
                  variant="outlined"
                  value={email}
                  onChange={handleEmailChange}
                  onKeyDown={handleKeyDown}
                  size="small"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    width: '100%',
                    margin: '0 auto',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#D9D9D9' },
                      '&:hover fieldset': { borderColor: '#D9D9D9' },
                      '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '16px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#000000',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#B3B3B3',
                    },
                  }}
                />
              </div>
    
              {/* PASSWORD Field */}
              <div className={styles.loginFormGroupCentered}>
                <Typography
                  sx={{
                    color: '#1E1E1E',
                    fontSize: '16px',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    lineHeight: '22.4px',
                    marginBottom: '8px',
                  }}
                >
                  Password
                </Typography>
                <TextField
                  placeholder="password123"
                  variant="outlined"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyDown={handleKeyDown}
                  size="small"
                  sx={{
                    backgroundColor: '#fff',
                    borderRadius: '8px',
                    width: '100%',
                    margin: '0 auto',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': { borderColor: '#D9D9D9' },
                      '&:hover fieldset': { borderColor: '#D9D9D9' },
                      '&.Mui-focused fieldset': { borderColor: '#D9D9D9' },
                    },
                    '& .MuiOutlinedInput-input': {
                      fontSize: '16px',
                      fontFamily: 'Inter, sans-serif',
                      color: '#000000',
                    },
                    '& .MuiOutlinedInput-input::placeholder': {
                      color: '#B3B3B3',
                    },
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={toggleShowPassword} edge="end">
                          {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                        </IconButton>
                      </InputAdornment>
                    )
                  }}
                />
              </div>

              {/* Login Button */}
              <Button
                variant="contained"
                onClick={handleLogin}
                className={styles.loginButton}
                fullWidth
              >
                Login
              </Button>

              {/* Forgot Password and Sign Up Links (no gap) */}
              <div className={styles.loginLinksStacked}>
                <Typography variant="body2" className={styles.linkWrapper}>
                  <MuiLink 
                    component="button" 
                    onClick={handleForgotPassword} 
                    underline="always"
                    className={styles.simpleLink}
                  >
                    Forgot Password?
                  </MuiLink>
                </Typography>
                <Typography variant="body2" className={styles.linkWrapper}>
                  Don&apos;t have an account?{' '}
                  <MuiLink 
                    component={RouterLink} 
                    to="/signup" 
                    underline="always"
                    className={styles.simpleLink}
                  >
                    Sign Up Here
                  </MuiLink>
                </Typography>
              </div>

              {/* Error Message */}
              {error && (
                <Typography color="error" sx={{ marginTop: '10px' }}>
                  {error}
                </Typography>
              )}
            </div>
          </div>
        </section>
  
        {/* Footer */}
        <footer className={styles.footer}>
          <span className={styles.footerText}>
            GigemAI
          </span>
        </footer>
      </main>
    );
}

export default Login;